import { Suspense } from "react";

import { useLocalizedHref } from "@libraries/web-framework";
import { defineMessages, useIntl } from "react-intl";

import Pricing, { type PricingCardProps } from "@/components/wpl/pricing/src";

import {
    PricingCollection,
    useLocalizedPriceData
} from "../../../../contentful";

export interface PricingSectionProps {
    serverSidePricing?: PricingCollection;
}

export interface PriceBlockProps {
    pricing: PricingCollection;
}

const PriceBlock = ({ pricing }: PriceBlockProps) => {
    const intl = useIntl();
    const messages = defineMessages({
        header: {
            id: "ConfluenceSoftwarePageTemplate.pricing.header",
            defaultMessage: "Prices that scale, for teams of all sizes"
        },
        tierFree: {
            id: "ConfluenceSoftwarePageTemplate.pricing.tierFree",
            defaultMessage: "Free"
        },
        tierStandard: {
            id: "ConfluenceSoftwarePageTemplate.pricing.tierStandard",
            defaultMessage: "Standard"
        },
        tierPremium: {
            id: "ConfluenceSoftwarePageTemplate.pricing.tierPremium",
            defaultMessage: "Premium"
        },
        costDisplayValue: {
            id: "ConfluenceSoftwarePageTemplate.pricing.costDisplayValue",
            defaultMessage: "{unit} {value}"
        },
        costUnitAndDuration: {
            id: "ConfluenceSoftwarePageTemplate.pricing.costUnitAndDuration",
            defaultMessage: "per user/month"
        },
        tierCostUpTo: {
            id: "ConfluenceSoftwarePageTemplate.pricing.tierCostUpTo",
            defaultMessage: "up to {max} users"
        },
        tierCostRange: {
            id: "ConfluenceSoftwarePageTemplate.pricing.tierCostRange",
            defaultMessage: "{min}-{max} users"
        },
        getStartedCta: {
            id: "ConfluenceSoftwarePageTemplate.pricing.getStartedCta",
            defaultMessage: "Get started"
        },
        startTrialCta: {
            id: "ConfluenceSoftwarePageTemplate.pricing.startTrialCta",
            defaultMessage: "Start trial"
        },
        freeForever: {
            id: "ConfluenceSoftwarePageTemplate.pricing.freeForever",
            defaultMessage: "Free forever"
        },
        noObligation: {
            id: "ConfluenceSoftwarePageTemplate.pricing.noObligation",
            defaultMessage: "No obligation"
        },
        freeSevenDays: {
            id: "ConfluenceSoftwarePageTemplate.pricing.freeSevenDays",
            defaultMessage: "Free for 7 days"
        },
        noCardNeeded: {
            id: "ConfluenceSoftwarePageTemplate.pricing.noCardNeeded",
            defaultMessage: "No credit card needed"
        },
        learnMore: {
            id: "ConfluenceSoftwarePageTemplate.pricing.learnMore",
            defaultMessage: "Learn more"
        },
        footerManyUsers: {
            id: "ConfluenceSoftwarePageTemplate.pricing.footerManyUsers",
            defaultMessage: "101+ users?"
        },
        footerDiscounts: {
            id: "ConfluenceSoftwarePageTemplate.pricing.footerDiscounts",
            defaultMessage: "We offer discounts for large teams!"
        },
        footerCalculatePrice: {
            id: "ConfluenceSoftwarePageTemplate.pricing.footerCalculatePrice",
            defaultMessage: "Calculate your price"
        }
    });

    const linkUrls = {
        freeCtaUrl: useLocalizedHref(
            "/try/cloud/signup?bundle=confluence&edition=free"
        ),
        standardCtaUrl: useLocalizedHref(
            "/try/cloud/signup?bundle=confluence&edition=standard"
        ),
        premiumCtaUrl: useLocalizedHref(
            "/try/cloud/signup?bundle=confluence&edition=premium"
        ),
        learnMoreStandardUrl: useLocalizedHref("/software/confluence/standard"),
        learnMorePremiumUrl: useLocalizedHref("/software/confluence/premium"),
        footerCalculatePriceUrl: useLocalizedHref(
            "/software/confluence/pricing#pricing-calculator"
        )
    };

    const cards: PricingCardProps[] =
        pricing && pricing.free && pricing.standard && pricing.premium
            ? [
                  {
                      ctaEmphasis: true,
                      tier: intl.formatMessage(messages.tierFree),
                      cost: intl.formatMessage(messages.costDisplayValue, {
                          unit: "USD",
                          value: pricing.free[0].prices.find(
                              (price) => price.currency == "USD"
                          )?.unit_price
                      }),
                      costLabel: intl.formatMessage(
                          messages.costUnitAndDuration
                      ),
                      quantity: intl.formatMessage(messages.tierCostUpTo, {
                          max: pricing.free[0].unit_limit_upper
                      }),
                      cta: intl.formatMessage(messages.getStartedCta),
                      ctaUrl: linkUrls.freeCtaUrl,
                      description1: intl.formatMessage(messages.freeForever),
                      description2: intl.formatMessage(messages.noObligation)
                  },
                  {
                      ctaEmphasis: false,
                      tier: intl.formatMessage(messages.tierStandard),
                      cost: intl.formatMessage(messages.costDisplayValue, {
                          unit: "USD",
                          value: pricing.standard[0].prices.find(
                              (price) => price.currency == "USD"
                          )?.unit_price
                      }),
                      costLabel: intl.formatMessage(
                          messages.costUnitAndDuration
                      ),
                      quantity: intl.formatMessage(messages.tierCostRange, {
                          min: Math.max(
                              pricing.standard[0].unit_limit_lower,
                              1
                          ),
                          max: pricing.standard[0].unit_limit_upper
                      }),
                      cta: intl.formatMessage(messages.startTrialCta),
                      ctaUrl: linkUrls.standardCtaUrl,
                      description1: intl.formatMessage(messages.freeSevenDays),
                      description2: intl.formatMessage(messages.noCardNeeded),
                      linkText: intl.formatMessage(messages.learnMore),
                      linkUrl: linkUrls.learnMoreStandardUrl
                  },
                  {
                      ctaEmphasis: false,
                      tier: intl.formatMessage(messages.tierPremium),
                      cost: intl.formatMessage(messages.costDisplayValue, {
                          unit: "USD",
                          value: pricing.premium[0].prices.find(
                              (price) => price.currency == "USD"
                          )?.unit_price
                      }),
                      costLabel: intl.formatMessage(
                          messages.costUnitAndDuration
                      ),
                      quantity: intl.formatMessage(messages.tierCostRange, {
                          min: Math.max(pricing.premium[0].unit_limit_lower, 1),
                          max: pricing.premium[0].unit_limit_upper
                      }),
                      cta: intl.formatMessage(messages.startTrialCta),
                      ctaUrl: linkUrls.premiumCtaUrl,
                      description1: intl.formatMessage(messages.freeSevenDays),
                      description2: intl.formatMessage(messages.noCardNeeded),
                      linkText: intl.formatMessage(messages.learnMore),
                      linkUrl: linkUrls.learnMorePremiumUrl
                  }
              ]
            : [];

    return (
        <section>
            <Suspense>
                {pricing && (
                    <>
                        <Pricing
                            header={intl.formatMessage(messages.header)}
                            cards={cards}
                            footer={
                                <p>
                                    <strong>
                                        {intl.formatMessage(
                                            messages.footerManyUsers
                                        )}
                                    </strong>{" "}
                                    &nbsp;{" "}
                                    {intl.formatMessage(
                                        messages.footerDiscounts
                                    )}{" "}
                                    <a href={linkUrls.footerCalculatePriceUrl}>
                                        {intl.formatMessage(
                                            messages.footerCalculatePrice
                                        )}
                                    </a>
                                </p>
                            }
                        />
                    </>
                )}
            </Suspense>
        </section>
    );
};

const PriceBlockWithFetch = ({
    envSource
}: {
    envSource: NodeJS.ProcessEnv | Window;
}) => {
    const { prices, status } = useLocalizedPriceData(
        "pricingplan.confluence.ondemand",
        // @ts-ignore
        envSource["PRODUCT_CATALOG_DELIVERY_KEY"] as string,
        // @ts-ignore
        envSource["PRODUCT_CATALOG_SPACE"] as string
    );

    if (status == "complete" && prices) {
        return <PriceBlock pricing={prices} />;
    }
    //TODO: handle request loading state
    return <PriceBlock pricing={prices || {}} />;
};

const PricingSection = ({ serverSidePricing }: PricingSectionProps) => {
    if (serverSidePricing) {
        return <PriceBlock pricing={serverSidePricing} />;
    }
    // server-side request failed
    if (typeof window !== "undefined") {
        // client side render
        return <PriceBlockWithFetch envSource={window} />;
    }
    // server side render
    return <PriceBlockWithFetch envSource={process.env} />;
};

export default PricingSection;
